import {
  COMET_COLLAPSE_DELAY,
  COMET_DOT,
  COMET_RIBBONS,
  cometSatelliteBall,
  DOT_PEAK,
  DOT_R,
  DOT_X,
  NOTIF_ANGLE,
  NOTIF_DIST,
  NOTIF_MARGIN,
  NOTIF_POP,
  NOTIF_R,
  LOSI_TRAIL_STOPS,
  RINGS,
  SWOOSH,
  particles,
  type ArcSpec,
  type DotRender
} from './decor'
import { EYE_H, EYE_SPLIT, EYE_W, REST_GAZE, type HeadGaze } from './face'
import { LOIS_PERIOD, LOIS_SM_PATH_WIDTH, LOIS_SPARKLE_SM, SLEEP_SPARKLE_SCALE, logoPlanetSilhouette, sparkleStar } from './losi'
import { EXCLAIM_CYCLE, EXCLAIM_CYCLE_SLOT, blendIcons, iconDots, iconSilhouette } from './icons'
import { TAU, clamp, easings, lerp } from './math'
import {
  circle,
  hullOfCircles,
  polyPath,
  profileFromPolygon,
  silhouette,
  type Silhouette
} from './shape'

export interface EyeCfg {
  /** local width (short axis of the capsule), in ball-radius units */
  w: number
  /** local height (long axis) */
  h: number
  /** 1 = open, 0 = closed */
  open: number
  /**
   * The capsule's own tilt, in degrees; positive = the top leans to the right.
   * Applied AFTER the sphere's tangent frame. Without it, both eyes necessarily
   * lean the same way (head roll) and anger and sadness, which need mirrored
   * tilts, are out of reach.
   */
  tilt?: number
  /** `smile` = rounded top + cheek-pinched bottom; default stadium capsule. */
  form?: 'capsule' | 'smile'
}

export interface Pose {
  /** body silhouette, in ball-radius units */
  sil: Silhouette
  /** global offset of the body AND the eyes */
  offX: number
  offY: number
  gaze: HeadGaze
  /** half eye spacing on the sphere, in degrees */
  split: number
  /** [inner eye, outer eye] */
  eyes: [EyeCfg, EyeCfg]
  /** eye opacity: used by faceless states */
  eyeAlpha: number
  bodyAlpha: number
  dots: DotRender[]
  arcs: ArcSpec[]
  notif: { x: number; y: number; r: number; notch: number } | null
  /** true = decor renders behind the body (burst particles) */
  dotsBehind: boolean
}

const pair = (w: number, h: number): [EyeCfg, EyeCfg] => [
  { w, h, open: 1 },
  { w, h, open: 1 }
]

/** Measured wink targets — the hold pose, not the opening frame. */
const WINK_GAZE: HeadGaze = { yaw: -5.37, pitch: 4.55, roll: 6.7 }
const WINK_SPLIT = 16.25
const WINK_OPEN_EYE = { w: 0.236, h: 0.464, open: 1 }
const WINK_SHUT_EYE = { w: 0.447, h: 0.089, open: 1 }

function base(over: Partial<Pose> = {}): Pose {
  return {
    sil: circle(1),
    offX: 0,
    offY: 0,
    gaze: { ...REST_GAZE },
    split: EYE_SPLIT,
    eyes: pair(EYE_W, EYE_H),
    eyeAlpha: 1,
    bodyAlpha: 1,
    dots: [],
    arcs: [],
    notif: null,
    dotsBehind: false,
    ...over
  }
}

/** Forward-facing agent gaze on the star — level eyes, no video head-tilt. */
const AGENT_GAZE: HeadGaze = { yaw: 0, pitch: 8, roll: 0 }

function agentBase(over: Partial<Pose> = {}): Pose {
  const { gaze, ...rest } = over
  return base({
    gaze: { ...AGENT_GAZE, ...gaze },
    ...rest
  })
}

/**
 * When horizontal travel is `cos(phase)`, velocity is `-sin(phase)` — gaze leads the sweep
 * so left/right reads match motion on concave shapes (star waist).
 */
function gazeAlongTravel(phase: number, yawAmp: number, pitchAmp = 0): HeadGaze {
  const yaw = -Math.sin(phase) * yawAmp
  return {
    yaw,
    pitch: pitchAmp * Math.cos(phase) + yaw * 0.38,
    roll: 0
  }
}

/** When yaw position follows `sin(phase)`, velocity is `cos(phase)`. */
function gazeAlongSinTravel(phase: number, yawAmp: number, pitch = 0): HeadGaze {
  const yaw = Math.cos(phase) * yawAmp
  return {
    yaw,
    pitch: pitch + yaw * 0.38,
    roll: 0
  }
}

/* --------------------------------------------------- non-radial shapes */

/**
 * Upright "!" bar: convex hull of two circles.
 * Measured: top circle (0, -0.505) r 0.132, bottom circle (0, +0.130) r 0.075,
 * straight flanks. It is therefore frustum-shaped (top/bottom ratio 1.76).
 */
const BAR_UPRIGHT_CY = -0.1875
const BAR_UPRIGHT = profileFromPolygon(
  hullOfCircles(0, -0.505, 0.132, 0, 0.13, 0.075),
  0,
  BAR_UPRIGHT_CY
)

/** Italic "!" bar: pure capsule (constant width 0.269, length 0.776). */
const BAR_ITALIC = profileFromPolygon(hullOfCircles(0, -0.2535, 0.1345, 0, 0.2535, 0.1345), 0, 0)

const barUpright = (pose: Partial<Silhouette> = {}): Silhouette => ({
  radii: [...BAR_UPRIGHT],
  rot: 0,
  cx: 0,
  cy: BAR_UPRIGHT_CY,
  sx: 1,
  sy: 1,
  ...pose
})

const barItalic = (pose: Partial<Silhouette> = {}): Silhouette => ({
  radii: [...BAR_ITALIC],
  rot: 0,
  cx: 0,
  cy: 0,
  sx: 1,
  sy: 1,
  ...pose
})

/**
 * The italic "!" dot is not a disk: it is a teardrop, rounded end (r 0.118) on
 * the bar side and a tapered point on the opposite side, length 0.300 along the
 * glyph axis. Centered on the rounded end's barycentre.
 */
const TEAR = polyPath(hullOfCircles(0, 0, 0.118, 0, 0.172, 0.012))

/**
 * The play/orbit star does not spin on itself: its centre traces a circle of
 * radius 0.213 around the origin (measured on the triangle; kept for the star).
 */
const TRI_ORBIT = 0.213

/** Tipping motion measured from the video, on the customiser sparkle profile. */
function spinningSparkle(rot: number): Silhouette {
  return sparkleStar(1, {
    rot,
    cx: -TRI_ORBIT * Math.sin(rot),
    cy: TRI_ORBIT * Math.cos(rot)
  })
}

/** Sleep planet: logo-scale sparkle, gentle breath (not the Lottie 143% pulse). */
function sleepPlanet(t: number): Pose {
  const breath = 1 + 0.06 * Math.sin((t / LOIS_PERIOD) * TAU)
  return base({
    sil: sparkleStar(SLEEP_SPARKLE_SCALE * breath),
    eyeAlpha: 0
  })
}

/**
 * Logo planet: breathes size + shape together (sleep star ⇄ sharp logotype).
 */
function logoPlanet(t: number): Pose {
  return base({
    sil: logoPlanetSilhouette(t),
    eyeAlpha: 0
  })
}

/**
 * Catalogue orbit kinematics. Spin numbers (1.25 turns/s, 0.35 s ramp, body
 * recentre 1.6–2.5) are measured. The coda after that is designed: the star
 * eases to rest, then turns onto a 4-lobe axis (idle sparkle). Montage holds
 * that pose; it does not keep spinning.
 */
const ORBIT_TURNS = 1.25
const ORBIT_RAMP = 0.35
const ORBIT_BACK_AT = 1.6
const ORBIT_BACK = 0.9
const ORBIT_STOP = 1.1
const ORBIT_LAND = 0.6
const ORBIT_CRUISE_END = ORBIT_BACK_AT + ORBIT_BACK
const ORBIT_DURATION = ORBIT_CRUISE_END + ORBIT_STOP + ORBIT_LAND

function orbitBrakeU(t: number): number {
  return clamp((t - ORBIT_CRUISE_END) / ORBIT_STOP)
}

function orbitLandK(t: number): number {
  return easings.easeOutQuint(clamp((t - (ORBIT_CRUISE_END + ORBIT_STOP)) / ORBIT_LAND))
}

/**
 * Wall time on the cruise, then the integral of `(1-u)²` so angular velocity
 * matches at the brake and reaches 0 with a still derivative. Rings read this
 * so they slow with the star instead of skating on after it has stopped.
 */
function orbitPhaseTime(t: number): number {
  if (t <= ORBIT_CRUISE_END) return t
  const u = orbitBrakeU(t)
  return ORBIT_CRUISE_END + ORBIT_STOP * (u - u * u + (u * u * u) / 3)
}

function orbitRot(t: number): number {
  const speed = ORBIT_TURNS * TAU
  const ramp = easings.easeInOutCubic(clamp(t / ORBIT_RAMP))
  return -speed * orbitPhaseTime(t) * ramp
}

/** Four-lobe sparkle is identical every π/2; idle is that family, rot = 0. */
function orbitUprightRot(): number {
  const stopped = orbitRot(ORBIT_CRUISE_END + ORBIT_STOP)
  const q = Math.PI / 2
  return Math.round(stopped / q) * q
}

function orbitSilRot(t: number): number {
  const spinning = orbitRot(t)
  const land = orbitLandK(t)
  if (land <= 0) return spinning
  return lerp(orbitRot(ORBIT_CRUISE_END + ORBIT_STOP), orbitUprightRot(), land)
}

/** 1 = satellite still orbiting, 0 = parked at the logotype rest. */
export function orbitUniverseSettle(local: number): number {
  return (1 - orbitLandK(local)) * easings.easeInOutCubic(orbitBrakeU(local))
}

/** Opacity of the Losi ring + satellite during the orbit coda. */
export function orbitUniverseFade(local: number): number {
  const land = orbitLandK(local)
  if (land > 0) return land
  return 1 - easings.easeInCubic(orbitBrakeU(local))
}

/* ------------------------------------------------------------------ states */

export type StateId =
  | 'idle'
  | 'thinking'
  | 'wink'
  | 'wide'
  | 'alert'
  | 'notify'
  | 'exclaim'
  | 'sleep'
  | 'logo'
  | 'logoOuter'
  | 'egg'
  | 'hexagon'
  | 'play'
  | 'orbit'
  | 'burst'
  | 'comet'
  /** loader-only loops: outside `SEQUENCE`, offered in the animations palette */
  | 'connect'
  | 'glyph'
  | 'icons'
  | 'bounce'
  | 'wobble'
  | 'pulse'
  | 'sway'
  | 'nod'
  | 'shake'
  | 'spin'
  | 'blink'
  | 'hop'
  | 'drift'
  | 'inflate'
  | 'jiggle'
  /** agent-only loops: outside `SEQUENCE` and `LOADER_SEQUENCE`, for `<LosiAgent />` */
  | 'agentIdle'
  | 'agentThink'
  | 'agentRun'
  | 'agentTool'
  | 'agentConnect'
  | 'agentSearch'
  | 'agentStream'
  | 'agentWait'
  | 'agentOk'
  | 'agentFail'
  | 'agentCancel'
  | 'agentHandoff'
  | 'agentPair'
  | 'agentReview'
  | 'agentSync'
  | 'agentChat'
  /** interface transition, not a catalogue animation: outside `SEQUENCE` */
  | 'swirl'

export interface StateDef {
  id: StateId
  /** hold duration when the full sequence is played */
  duration: number
  /**
   * duration below which the animation is cut before completing: the "!" does not
   * return, the body stays burst. Read from the `pose` constants below, not
   * chosen arbitrarily. Absent = the state ignores timing or loops; any duration
   * works (see `MIN_BLOCK`).
   */
  minDuration?: number
  /** entry morph duration */
  morph: number
  /** true = entry is masked by a blink, as in the video */
  blinkIn: boolean
  /**
   * true = the body is the "at rest" silhouette, so replaceable by the shape
   * chosen in the customiser. States that draw their own shape (the "!", dots,
   * egg, triangle...) are false: that shape IS the animation.
   */
  baseBody: boolean
  /**
   * true = the state carries the "at rest" face, so replaceable by the chosen
   * expression. Only `idle`: other face states have an expression measured off
   * the video; that is precisely what we reproduce.
   */
  baseFace: boolean
  pose(local: number): Pose
}

/** Pulse wave that travels across the three dots from left to right. */
function dotPulse(t: number, index: number): number {
  const p = ((((t - index * 0.5) / 1.5) % 1) + 1) % 1
  const k = p < 0.5 ? 0.5 - 0.5 * Math.cos(p * TAU) : 0
  return clamp(k * 2)
}

/**
 * Synthetic agent activity rings. These belong only to `agentRun` and
 * `agentTool`; they do not reuse or alter the measured catalogue orbit data.
 */
const AGENT_RUN_ORBIT = {
  a: 1.16,
  k: 0.16,
  tilt: -0.58,
  speed: 0.5,
  phase: 0,
  sweep: 0.68,
  hue: 204,
  hueSpan: 28,
  width: 0.034,
  cx: 0,
  cy: 0
}
const AGENT_TOOL_ORBIT = {
  a: 1.08,
  k: 0.04,
  tilt: 0.78,
  speed: 0.5,
  phase: 0,
  sweep: 0.25,
  hue: 38,
  hueSpan: 30,
  width: 0.034,
  cx: 0,
  cy: 0
}

/**
 * Connecting to external tools / integrations — three ports around the hub,
 * staggered handshake pulses (synthetic; not measured catalogue rings).
 * Cream trails + ink accents — not the old gold/teal/purple rainbow ports.
 */
const CONNECT_PERIOD = 3.2
const CONNECT_PORTS = [
  { ang: -0.62, dist: 1.5 },
  { ang: 1.48, dist: 1.46 },
  { ang: 3.58, dist: 1.52 }
] as const
const CONNECT_ARCS = CONNECT_PORTS.map((p, i) => ({
  a: 1.1,
  k: 0.05,
  tilt: p.ang,
  speed: 0.38,
  phase: i * 1.15,
  sweep: 0.2,
  hue: 0,
  hueSpan: 0,
  width: 0.03,
  cx: 0,
  cy: 0,
  stops: [...LOSI_TRAIL_STOPS]
}))
const CONNECT_NODE_R = 0.1

/** Soft 0…1 pulse per integration port — continuous sine, staggered by third. */
function connectPulse(t: number, index: number): number {
  const u = (((t / CONNECT_PERIOD) - index / CONNECT_PORTS.length) % 1 + 1) % 1
  return 0.5 - 0.5 * Math.cos(u * TAU)
}

function connectGaze(t: number, emerge: number): HeadGaze {
  const pulses = CONNECT_PORTS.map((_, i) => connectPulse(t, i))
  let best = 0
  for (let i = 1; i < pulses.length; i++) {
    if (pulses[i]! > pulses[best]!) best = i
  }
  const ang = CONNECT_PORTS[best]!.ang
  return {
    yaw: Math.sin(ang) * 18 * emerge,
    pitch: 4 - Math.cos(ang) * 7 * emerge,
    roll: 0
  }
}

function connectArcs(t: number, emerge: number): ArcSpec[] {
  return CONNECT_ARCS.map((seed, i) => ({
    id: `cn${i}`,
    seed,
    t,
    opacity: (0.2 + 0.48 * connectPulse(t, i)) * emerge
  }))
}

function connectNodes(t: number, emerge: number): DotRender[] {
  return CONNECT_PORTS.map((p, i) => {
    const k = connectPulse(t, i)
    const r = CONNECT_NODE_R * (0.82 + 0.38 * k)
    const d = p.dist * emerge
    return {
      x: Math.sin(p.ang) * d,
      y: -Math.cos(p.ang) * d,
      r,
      opacity: (0.38 + 0.55 * k) * emerge,
      d: LOIS_SPARKLE_SM,
      pathScale: (r * 2) / LOIS_SM_PATH_WIDTH,
      accent: true
    }
  })
}

const AGENT_SEARCH_HORIZONTAL = {
  a: 1.05,
  k: 0.08,
  tilt: 0,
  speed: 0.45,
  phase: 0,
  sweep: 0.82,
  hue: 205,
  hueSpan: 35,
  width: 0.042,
  cx: 0,
  cy: 0
}
const AGENT_SEARCH_VERTICAL = {
  ...AGENT_SEARCH_HORIZONTAL,
  tilt: Math.PI / 2,
  speed: 0.25,
  phase: 0
}

export const STATES: StateDef[] = [
  {
    id: 'idle',
    duration: 2.4,
    morph: 0.45,
    blinkIn: false,
    baseFace: true,
    baseBody: true,
    pose: () => base()
  },

  {
    id: 'thinking',
    duration: 2.6,
    morph: 0.4,
    baseFace: false,
    baseBody: false,
    blinkIn: true,
    pose: (t) => {
      const mid = dotPulse(t, 1)
      // Side dots emerge from the ball's flanks: in the video they stay merged
      // with it for 1–2 frames before detaching.
      const emerge = 0.3 + 0.7 * easings.easeOutCubic(clamp(t / 0.3))
      return base({
        // Middle dot is the sparkle; left and right stay round like the reference.
        sil: sparkleStar(DOT_R * (1 + (DOT_PEAK - 1) * mid), { cx: DOT_X[1]! }),
        eyeAlpha: 0,
        dots: [0, 2].map((i) => {
          const k = dotPulse(t, i)
          return {
            x: DOT_X[i]! * emerge,
            y: 0,
            r: DOT_R * (1 + (DOT_PEAK - 1) * k),
            opacity: 0.55 + 0.45 * k
          }
        })
      })
    }
  },

  {
    id: 'wink',
    duration: 1.6,
    morph: 0.3,
    blinkIn: true,
    baseFace: false,
    baseBody: true,
    pose: (t) => {
      const close = 0.32
      const open = 0.28
      const dur = 1.6
      t = Math.min(t, dur)
      let k = 0
      if (t < close) k = easings.easeInOutCubic(t / close)
      else if (t > dur - open) k = easings.easeInOutCubic((dur - t) / open)
      else k = 1

      const lerpEye = (a: EyeCfg, b: EyeCfg) => ({
        w: a.w + (b.w - a.w) * k,
        h: a.h + (b.h - a.h) * k,
        open: 1 as const
      })
      const rest = pair(EYE_W, EYE_H)
      return base({
        gaze: {
          yaw: REST_GAZE.yaw + (WINK_GAZE.yaw - REST_GAZE.yaw) * k,
          pitch: REST_GAZE.pitch + (WINK_GAZE.pitch - REST_GAZE.pitch) * k,
          roll: REST_GAZE.roll + (WINK_GAZE.roll - REST_GAZE.roll) * k
        },
        split: EYE_SPLIT + (WINK_SPLIT - EYE_SPLIT) * k,
        eyes: [lerpEye(rest[0]!, WINK_OPEN_EYE), lerpEye(rest[1]!, WINK_SHUT_EYE)]
      })
    }
  },

  {
    id: 'wide',
    duration: 1.8,
    morph: 0.55,
    blinkIn: true,
    baseFace: false,
    baseBody: true,
    pose: () =>
      base({
        gaze: { yaw: 6.92, pitch: -21.96, roll: 11.6 },
        split: 18.43,
        eyes: pair(0.356, 0.875)
      })
  },

  {
    id: 'alert',
    duration: 2.4,
    // the "!" returns to place at 1.6 + 0.4
    minDuration: 2,
    morph: 0.45,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => {
      // Measured travel: -0.087 -> +0.732 in 1.5 s, ease-in-out, micro-overshoot.
      const p = clamp(t / 1.5)
      const travel = easings.easeInOutCubic(p) * 0.82 - 0.087
      const back = t > 1.6 ? clamp((t - 1.6) / 0.4) : 0
      const x = travel * (1 - back) + 0.1 * back
      // Secondary vibration at 2.5 Hz, bar and dot in opposite phase.
      const buzz = Math.sin(t * 2.5 * TAU) * 0.005
      const tilt = (17.7 * Math.PI) / 180
      return base({
        sil: barItalic({ rot: tilt, cx: x, cy: -0.325 - buzz }),
        eyeAlpha: 0,
        dots: [
          {
            // the dot follows the glyph axis, 0.580 from the bar centre
            x: x - Math.sin(tilt) * 0.58,
            y: -0.325 + Math.cos(tilt) * 0.58 + buzz * 2.8,
            r: 0.118,
            d: TEAR,
            rot: (tilt * 180) / Math.PI,
            opacity: 1
          }
        ]
      })
    }
  },

  {
    id: 'notify',
    duration: 2.2,
    morph: 0.5,
    blinkIn: true,
    baseFace: false,
    baseBody: true,
    pose: (t) => {
      // Blue dot pop: peak at +14 % around 0.3 s then stabilisation.
      const p = clamp(t / 0.45)
      const pop = 1 + (NOTIF_POP - 1) * Math.sin(p * Math.PI) * (1 - p * 0.35)
      const r = NOTIF_R * (p < 1 ? pop : 1)
      const a = (NOTIF_ANGLE * Math.PI) / 180
      return base({
        // gaze goes opposite the badge
        gaze: { yaw: -21.94, pitch: -5.82, roll: -12.2 },
        split: 18.89,
        eyes: pair(0.505, 0.498),
        notif: {
          x: Math.cos(a) * NOTIF_DIST,
          y: Math.sin(a) * NOTIF_DIST,
          r,
          notch: r + NOTIF_MARGIN
        }
      })
    }
  },

  {
    id: 'exclaim',
    duration: 2,
    morph: 0.45,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: () =>
      base({
        sil: barUpright(),
        eyeAlpha: 0,
        dots: [{ x: -0.012, y: 0.526, r: 0.113, opacity: 1 }]
      })
  },

  {
    id: 'sleep',
    duration: 2.4,
    morph: 0.5,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => sleepPlanet(t)
  },

  /**
   * Logotype as a bot state: sleep's planet with the Lottie pulse + curve,
   * satellite revolving outside the oval (same rest corner as the mark).
   */
  {
    id: 'logo',
    duration: LOIS_PERIOD,
    morph: 0.5,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => logoPlanet(t)
  },

  /**
   * Clone of `logo`: ring stays, satellite keeps revolving outside the oval.
   */
  {
    id: 'logoOuter',
    duration: LOIS_PERIOD,
    morph: 0.5,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => logoPlanet(t)
  },

  {
    id: 'egg',
    duration: 1.8,
    morph: 0.4,
    baseFace: false,
    baseBody: false,
    blinkIn: true,
    pose: () =>
      base({
        sil: silhouette('egg'),
        gaze: { yaw: 19.97, pitch: 26.01, roll: -17.1 },
        // eyes narrow along with the body
        split: 11.07,
        eyes: pair(0.164, 0.385)
      })
  },

  {
    id: 'hexagon',
    duration: 1.6,
    morph: 0.4,
    baseFace: false,
    baseBody: false,
    blinkIn: true,
    pose: () =>
      base({
        sil: silhouette('hexagon'),
        gaze: { yaw: 23.11, pitch: 24.42, roll: -13.3 },
        split: 13.37,
        eyes: pair(0.177, 0.411)
      })
  },

  {
    id: 'play',
    duration: 2,
    morph: 0.5,
    baseFace: false,
    baseBody: false,
    blinkIn: true,
    pose: (t) => {
      // The star stays nearly still while the swoosh passes through it.
      const fade = clamp(t / 0.35) * clamp((2.2 - t) / 0.5)
      return base({
        sil: spinningSparkle(0),
        gaze: { yaw: 12, pitch: -8, roll: -6 },
        split: 15,
        eyes: pair(0.18, 0.34),
        // the swoosh sweeps right to left over the star
        arcs: SWOOSH.map((s, i) => ({
          id: `sw${i}`,
          seed: { ...s, cx: 0.45 - t * 0.42 },
          t,
          opacity: fade
        }))
      })
    }
  },

  {
    id: 'orbit',
    duration: ORBIT_DURATION,
    // centred at 2.5, spin at rest at 3.6, rest face at 4.2
    minDuration: ORBIT_DURATION,
    morph: 0.6,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => {
      t = Math.min(Math.max(0, t), ORBIT_DURATION)
      const rot = orbitSilRot(t)
      // Body relaxes from tipping sparkle to centred star during the orbit.
      const back = easings.easeInOutCubic(clamp((t - ORBIT_BACK_AT) / ORBIT_BACK))
      const land = orbitLandK(t)
      const tipped = spinningSparkle(rot)
      const centred = sparkleStar(1, { rot })
      const sil: Silhouette = {
        radii: tipped.radii.map((r, i) => r + (centred.radii[i]! - r) * back),
        rot,
        cx: tipped.cx * (1 - back),
        cy: tipped.cy * (1 - back),
        sx: 1,
        sy: 1
      }
      const brake = orbitBrakeU(t)
      const fade = clamp(t / 0.8) * (1 - easings.easeInCubic(brake)) * (1 - land)
      const gaze = {
        yaw: REST_GAZE.yaw + Math.sin(t * 6.5) * 65 * (1 - back),
        pitch: -4 + back * 32,
        roll: -13
      }
      return base({
        sil,
        gaze: {
          yaw: lerp(gaze.yaw, REST_GAZE.yaw, land),
          pitch: lerp(gaze.pitch, REST_GAZE.pitch, land),
          roll: lerp(gaze.roll, REST_GAZE.roll, land)
        },
        eyes: pair(lerp(0.18, EYE_W, land), lerp(0.34 + back * 0.07, EYE_H, land)),
        // rings enter one by one over 0.8 s, then slow and fade with the star
        arcs: RINGS.map((s, i) => ({
          id: `rg${i}`,
          seed: s,
          t: orbitPhaseTime(t),
          opacity: fade * clamp((t - i * 0.13) / 0.3)
        }))
      })
    }
  },

  {
    /**
     * Entry into the settings view.
     *
     * ONLY state not measured off the video: it is CHOSEN, like the `--ink`
     * colour. It borrows `orbit`'s vocabulary — the same rings, with their
     * measured parameters — but cuts short: 1 s instead of the catalogue orbit, half the rings,
     * and no triangle.
     *
     * The two `true` flags are the whole point of this state:
     *
     * - `baseBody` lets the chosen shape replace the body, so the view can
     *   impose the circle and the pebble or teardrop MORPH into it instead of
     *   jumping;
     * - `baseFace` carries the resting face, so cursor tracking applies from
     *   this entry. A state with its own gaze pose (like `orbit`) would hand
     *   control back mid-motion, and the eyes would jump on resume.
     *
     * Deliberately NOT in `SEQUENCE`: not a catalogue animation, but an
     * interface transition.
     */
    id: 'swirl',
    // slightly longer than the gaze turn (`TURN_TIME`, 1.1 s): eyes must be
    // settled left before the rings fade out
    duration: 1.3,
    minDuration: 1.3,
    morph: 0.3,
    baseFace: true,
    baseBody: true,
    // shape morph is masked by a blink, as everywhere else
    blinkIn: true,
    pose: (t) =>
      base({
        // three of `orbit`'s six rings: half the bouquet is enough to recognise
        // it, and that many fewer arcs to rasterise per frame
        arcs: RINGS.slice(0, 3).map((s, i) => ({
          id: `sw${i}`,
          seed: s,
          t,
          // they enter one after another then fade before the block ends, so
          // resuming idle lands on a clean frame
          opacity: clamp((t - i * 0.06) / 0.14) * clamp((1.22 - t) / 0.34)
        }))
      })
  },

  {
    id: 'burst',
    duration: 2.6,
    // body is reassembled at 1.7 + 0.7
    minDuration: 2.4,
    morph: 0.4,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => {
      // Measured collapse: 1.0 -> 0.166 in 0.7 s, ease-out, no bounce.
      const collapse = 1 - 0.834 * easings.easeOutQuint(clamp(t / 0.7))
      const regrow = easings.easeOutQuint(clamp((t - 1.7) / 0.7))
      return base({
        sil: sparkleStar(collapse + (1 - collapse) * regrow),
        eyeAlpha: clamp((t - 1.85) / 0.4),
        dots: particles(t, 1),
        dotsBehind: true
      })
    }
  },

  {
    id: 'comet',
    duration: 2.4,
    // dot reassembles at 1.85 + 0.6 = 2.45, i.e. 0.05 s after the video cut:
    // that tail finishes during the next fade, as in the reference. So we do
    // not go below the measured duration.
    minDuration: 2.4,
    morph: 0.45,
    baseFace: false,
    baseBody: false,
    blinkIn: false,
    pose: (t) => {
      const collapseT = Math.max(0, t - COMET_COLLAPSE_DELAY)
      const collapse = 1 - (1 - COMET_DOT) * easings.easeOutQuint(clamp(collapseT / 0.55))
      const regrow = easings.easeOutQuint(clamp((t - 1.85) / 0.6))
      const fade = clamp((collapseT - 0.15) / 0.25) * clamp((1.95 - t) / 0.3)
      const wobble = Math.sin(clamp(collapseT / 1.7) * Math.PI) * 0.035
      const sat = cometSatelliteBall(t)
      const trailDots: DotRender[] =
        fade > 0.05
          ? [
              {
                x: sat.x,
                y: sat.y + wobble,
                r: 0,
                d: LOIS_SPARKLE_SM,
                pathScale: (COMET_DOT * 2) / LOIS_SM_PATH_WIDTH,
                opacity: fade * 0.92
              }
            ]
          : []
      const scale = collapse + (1 - collapse) * regrow
      return base({
        sil: sparkleStar(scale, { cy: wobble }),
        // Eyes only once the planet has grown back — a mid-size sparkle clips them.
        eyeAlpha: clamp((scale - 0.92) / 0.08),
        arcs: COMET_RIBBONS.map((s, i) => ({ id: `cm${i}`, seed: s, t, opacity: fade })),
        dots: trailDots
      })
    }
  },

  {
    id: 'connect',
    duration: CONNECT_PERIOD,
    morph: 0.4,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const emerge = easings.easeOutCubic(clamp(t / 0.35))
      return base({
        arcs: connectArcs(t, emerge),
        dots: connectNodes(t, emerge),
        gaze: connectGaze(t, emerge)
      })
    }
  },

  /**
   * Static body icon at sparkle weight (bigger than the orbit oval). Default bang;
   * `engine.setIcon` / LosiBot `icon` prop morphs to another icon — it does not
   * auto-cycle through the catalogue.
   */
  {
    id: 'glyph',
    duration: 2.4,
    morph: 0.45,
    blinkIn: false,
    baseBody: false,
    baseFace: false,
    pose: () =>
      base({
        sil: iconSilhouette('bang'),
        eyeAlpha: 0,
        dots: iconDots('bang')
      })
  },

  /**
   * Exclamation cycle: upright bang ⇄ italic alert at sparkle weight.
   * Override with `icon` prop / `setIcon` to hold a fixed glyph instead.
   */
  {
    id: 'icons',
    duration: EXCLAIM_CYCLE.length * EXCLAIM_CYCLE_SLOT,
    morph: 0.45,
    blinkIn: false,
    baseBody: false,
    baseFace: false,
    pose: (t) => {
      const n = EXCLAIM_CYCLE.length
      const slot = EXCLAIM_CYCLE_SLOT
      const period = slot * n
      const local = ((t % period) + period) % period
      const i = Math.floor(local / slot)
      const u = (local % slot) / slot
      const hold = 0.45
      const k = u < hold ? 0 : easings.easeInOutCubic((u - hold) / (1 - hold))
      const a = EXCLAIM_CYCLE[i]!
      const b = EXCLAIM_CYCLE[(i + 1) % n]!
      const { sil, dots } = blendIcons(a, b, k)
      return base({ sil, dots, eyeAlpha: 0 })
    }
  },

  {
    id: 'bounce',
    duration: 2,
    morph: 0.4,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const p = (t / 2) % 1
      const y = Math.abs(Math.sin(p * Math.PI))
      const vy = Math.cos(p * Math.PI)
      const landing = y < 0.08 && vy < 0 ? (0.08 - y) / 0.08 : 0
      return base({
        offY: -0.22 * y,
        sil: circle(1, { sx: 1 + landing * 0.12, sy: 1 - landing * 0.1 })
      })
    }
  },

  {
    id: 'wobble',
    duration: 1.6,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const s = Math.sin((t / 1.6) * TAU)
      return base({
        sil: circle(1, { rot: s * 0.22 }),
        gaze: { yaw: s * 8, pitch: 0, roll: REST_GAZE.roll }
      })
    }
  },

  {
    id: 'pulse',
    duration: 1.2,
    morph: 0.35,
    blinkIn: false,
    baseBody: false,
    baseFace: false,
    pose: (t) => {
      const k = 0.5 + 0.5 * Math.sin((t / 1.2) * TAU)
      const scale = 0.88 + 0.16 * k
      return base({
        sil: circle(1, { sx: scale, sy: scale }),
        eyeAlpha: 0
      })
    }
  },

  {
    id: 'sway',
    duration: 2.4,
    morph: 0.4,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const s = Math.sin((t / 2.4) * TAU)
      return base({
        offX: s * 0.15,
        gaze: { yaw: s * 12, pitch: 0, roll: REST_GAZE.roll }
      })
    }
  },

  {
    id: 'nod',
    duration: 1.4,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const n = Math.sin((t / 1.4) * TAU)
      return base({
        offY: n * 0.05,
        gaze: { yaw: 0, pitch: n * 16, roll: REST_GAZE.roll }
      })
    }
  },

  {
    id: 'shake',
    duration: 1.8,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const envelope = 0.55 + 0.45 * Math.sin((t / 1.8) * TAU)
      const s = Math.sin(t * 14 * TAU) * 0.055 * envelope
      return base({
        offX: s,
        gaze: { yaw: s * 120, pitch: 0, roll: REST_GAZE.roll }
      })
    }
  },

  {
    id: 'spin',
    duration: 2,
    morph: 0.4,
    blinkIn: false,
    // Orbit kinematics only — `baseBody` swaps in the customiser / peer shape.
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const rot = ((t / 2) % 1) * TAU
      return base({
        sil: circle(1, {
          rot,
          cx: -TRI_ORBIT * Math.sin(rot),
          cy: TRI_ORBIT * Math.cos(rot)
        })
      })
    }
  },

  {
    id: 'blink',
    duration: 1.6,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const phase = (t / 0.55) % 1
      let open = 1
      if (phase < 0.18) open = 1 - phase / 0.18
      else if (phase < 0.28) open = 0
      else if (phase < 0.45) open = (phase - 0.28) / 0.17
      return base({
        eyes: [
          { w: EYE_W, h: EYE_H, open },
          { w: EYE_W, h: EYE_H, open }
        ]
      })
    }
  },

  {
    id: 'hop',
    duration: 1.2,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const p = (t / 1.2) % 1
      const y = Math.abs(Math.sin(p * Math.PI))
      return base({ offY: -0.14 * y })
    }
  },

  {
    id: 'drift',
    duration: 3,
    morph: 0.4,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const a = (t / 3) * TAU
      const c = Math.cos(a)
      const s = Math.sin(a)
      return base({
        offX: c * 0.13,
        offY: s * 0.09,
        gaze: { yaw: c * 11, pitch: s * 9, roll: REST_GAZE.roll }
      })
    }
  },

  {
    id: 'inflate',
    duration: 2.8,
    morph: 0.4,
    blinkIn: false,
    baseBody: false,
    baseFace: false,
    pose: (t) => {
      const k = 0.5 + 0.5 * Math.sin((t / 2.8) * TAU)
      const scale = 0.72 + 0.28 * k
      return base({
        sil: circle(1, { sx: scale, sy: scale }),
        eyeAlpha: 0
      })
    }
  },

  {
    id: 'jiggle',
    duration: 0.9,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const s = Math.sin((t / 0.9) * TAU * 2)
      return base({
        sil: circle(1, { rot: s * 0.14 }),
        gaze: { yaw: s * 6, pitch: 0, roll: REST_GAZE.roll }
      })
    }
  },

  /* ----------------------------------------------------------- agent UI */

  {
    id: 'agentIdle',
    duration: 2.8,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: true,
    pose: (t) => {
      const b = 0.5 + 0.5 * Math.sin((t / 2.8) * TAU)
      const s = 0.94 + 0.04 * b
      return agentBase({ sil: circle(1, { sx: s, sy: s }) })
    }
  },

  {
    id: 'agentThink',
    duration: 2.2,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 2.2) * TAU
      return agentBase({
        gaze: gazeAlongSinTravel(a, 9, 8)
      })
    }
  },

  {
    id: 'agentRun',
    duration: 1.8,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 1.8) * TAU
      return agentBase({
        offX: Math.cos(a) * 0.04,
        arcs: [{ id: 'ar', seed: AGENT_RUN_ORBIT, t, opacity: 0.58 }],
        gaze: gazeAlongTravel(a, 20, 5)
      })
    }
  },

  {
    id: 'agentTool',
    duration: 2.4,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 2.4) * TAU
      return agentBase({
        arcs: [{ id: 'at', seed: AGENT_TOOL_ORBIT, t, opacity: 0.58 }],
        gaze: gazeAlongSinTravel(a, 20, 8)
      })
    }
  },

  {
    id: 'agentConnect',
    duration: CONNECT_PERIOD,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const emerge = easings.easeOutCubic(clamp(t / 0.3))
      return agentBase({
        // Arcs only — agent UI keeps status symbols out of the bot render.
        arcs: connectArcs(t, emerge),
        gaze: connectGaze(t, emerge)
      })
    }
  },

  {
    id: 'agentSearch',
    duration: 2.6,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 2.6) * TAU
      return agentBase({
        arcs: [
          { id: 'ash', seed: AGENT_SEARCH_HORIZONTAL, t, opacity: 0.62 },
          { id: 'asv', seed: AGENT_SEARCH_VERTICAL, t, opacity: 0.42 }
        ],
        gaze: gazeAlongTravel(a, 22, 4)
      })
    }
  },

  {
    id: 'agentStream',
    duration: 1.5,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 1.5) * TAU
      return agentBase({
        gaze: gazeAlongSinTravel(a, 11, 4)
      })
    }
  },

  {
    id: 'agentWait',
    duration: 3.2,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const breath = Math.sin((t / 3.2) * TAU)
      const drift = Math.sin((t / 4.5) * TAU)
      const lid = 0.4 + 0.08 * (0.5 + 0.5 * Math.sin((t / 2.1) * TAU))
      return agentBase({
        sil: circle(1, { sx: 1 + breath * 0.02, sy: 1 + breath * 0.02 }),
        gaze: { yaw: drift * 6, pitch: 6 + breath * 2, roll: 0 },
        eyes: [
          { w: EYE_W * 0.94, h: EYE_H * 0.9, open: lid },
          { w: EYE_W * 0.94, h: EYE_H * 0.9, open: lid }
        ]
      })
    }
  },

  {
    id: 'agentOk',
    duration: 1.6,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const pop = t < 0.28 ? 1 + 0.08 * Math.sin((t / 0.28) * Math.PI) : 1
      return agentBase({
        offY: (pop - 1) * 0.35,
        gaze: { yaw: -4, pitch: 8, roll: 0 },
        eyes: pair(EYE_W * 0.92, EYE_H * 0.92)
      })
    }
  },

  {
    id: 'agentFail',
    duration: 1.8,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const sh = Math.sin(t * 14 * TAU) * 0.042 * (0.65 + 0.35 * Math.sin(t * 2.2))
      return agentBase({
        offX: sh,
        split: 17.5,
        eyes: pair(0.34, 0.76),
        gaze: { yaw: sh * 45, pitch: -12, roll: 0 }
      })
    }
  },

  {
    id: 'agentCancel',
    duration: 1.2,
    morph: 0.25,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: () =>
      agentBase({
        bodyAlpha: 0.28,
        eyeAlpha: 0.18,
        eyes: [
          { w: EYE_W, h: EYE_H, open: 0 },
          { w: EYE_W, h: EYE_H, open: 0 }
        ]
      })
  },

  {
    id: 'agentHandoff',
    duration: 1.5,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 1.5) * TAU
      return agentBase({
        gaze: gazeAlongTravel(a, 16, 0)
      })
    }
  },

  {
    id: 'agentPair',
    duration: 2.2,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const sway = Math.sin((t / 2.2) * TAU)
      return agentBase({
        gaze: { yaw: sway * 6, pitch: 4, roll: 0 }
      })
    }
  },

  {
    id: 'agentReview',
    duration: 2.4,
    morph: 0.35,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const a = (t / 2.4) * TAU
      return agentBase({
        gaze: gazeAlongTravel(a, 22, 4)
      })
    }
  },

  {
    id: 'agentSync',
    duration: 1.4,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const s = 0.5 + 0.5 * Math.sin((t / 1.4) * TAU)
      return agentBase({
        gaze: { yaw: 0, pitch: (s - 0.5) * 10, roll: 0 }
      })
    }
  },

  {
    id: 'agentChat',
    duration: 1.6,
    morph: 0.3,
    blinkIn: false,
    baseBody: true,
    baseFace: false,
    pose: (t) => {
      const nod = Math.sin((t / 1.6) * TAU) * 0.035
      return agentBase({
        offY: nod,
        gaze: { yaw: 14, pitch: nod * 80 + 6, roll: 0 }
      })
    }
  }
]

export const STATE_BY_ID = new Map(STATES.map((s) => [s.id, s]))

/** Playback order of the full sequence, aligned with the reference video. */
/**
 * Local-time instant where each state is most legible: the pose shown by
 * thumbnails and the state board. Deterministic render, so comparable run to
 * run. The type forces coverage of any new state.
 */
export const POSES: Record<StateId, number> = {
  idle: 1,
  thinking: 1.1,
  wink: 0.8,
  wide: 0.8,
  alert: 0.75,
  notify: 0.9,
  exclaim: 0.8,
  sleep: 0.45,
  logo: 0.5,
  logoOuter: 0.5,
  egg: 0.8,
  hexagon: 0.8,
  play: 0.9,
  orbit: 1.2,
  swirl: 0.5,
  burst: 0.45,
  comet: 1.15,
  connect: 1.1,
  glyph: 0.5,
  icons: 0.9,
  bounce: 0.5,
  wobble: 0.4,
  pulse: 0.3,
  sway: 0.25,
  nod: 0.35,
  shake: 0.4,
  spin: 0.35,
  blink: 0.22,
  hop: 0.35,
  drift: 0.5,
  inflate: 0.35,
  jiggle: 0.25,
  agentIdle: 0.35,
  agentThink: 0.55,
  agentRun: 0.25,
  agentTool: 0.4,
  agentConnect: 1.1,
  agentSearch: 0.45,
  agentStream: 0.18,
  agentWait: 0.4,
  agentOk: 1.1,
  agentFail: 0.35,
  agentCancel: 1,
  agentHandoff: 0.35,
  agentPair: 0.3,
  agentReview: 0.55,
  agentSync: 0.25,
  agentChat: 0.35
}

/** Loader loops built for UI spinners — not measured from the reference video. */
export const LOADER_SEQUENCE: StateId[] = [
  'logo',
  'logoOuter',
  'connect',
  'glyph',
  'icons',
  'bounce',
  'wobble',
  'pulse',
  'sway',
  'nod',
  'shake',
  'spin',
  'blink',
  'hop',
  'drift',
  'inflate',
  'jiggle'
]

/**
 * Agent-only loops for `<LosiAgent />` — distinct from catalogue and loaders.
 * Persisted in montages but not offered in the palette.
 */
export const AGENT_SEQUENCE: StateId[] = [
  'agentIdle',
  'agentThink',
  'agentRun',
  'agentTool',
  'agentConnect',
  'agentSearch',
  'agentStream',
  'agentWait',
  'agentOk',
  'agentFail',
  'agentCancel',
  'agentHandoff',
  'agentPair',
  'agentReview',
  'agentSync',
  'agentChat'
]

export const SEQUENCE: StateId[] = [
  'idle',
  'thinking',
  'wink',
  'wide',
  'alert',
  'notify',
  'exclaim',
  'sleep',
  'egg',
  'hexagon',
  'play',
  'orbit',
  'burst',
  'comet'
]

/** States the editor palette may append to a montage (video catalogue + loader loops). */
export const EDITABLE: StateId[] = [...SEQUENCE, ...LOADER_SEQUENCE]

/** Montage blocks that survive localStorage reload (palette + agent loops). */
export const PERSISTABLE: StateId[] = [...EDITABLE, ...AGENT_SEQUENCE]
